import cls from "@fvilers/cls";
import { FieldError } from "@imi/ui/components/FieldErrors/FieldErrors.js";
import { FiltersFieldRow } from "@imi/ui/components/Filters/Filters.js";
import { Select } from "@imi/ui/components/Select/Select.js";
import { Switch } from "@imi/ui/components/Switch/Switch.js";
import { SwitchLabel } from "@imi/ui/components/Switch/SwitchLabel.js";
import { useFieldContext } from "@imi/ui/hooks/useAppForm.js";
import { FilterFieldDefinition } from "@src/core/engine/helpers/fieldDefinitions/getFilterGroupsDefinition";
import { formatOptionLabel } from "@src/core/engine/helpers/fieldDefinitions/getOptionsFieldDefinition";
import { NumberField } from "@src/shared/components/NumberField/NumberField";
import { TFunction } from "i18next";
import styles from "./FilterField.module.css";

type FilterFieldProps = {
  className?: string;
  definition: FilterFieldDefinition;
  errors?: FieldError;
  onChange: (value: boolean | number | string) => void;
  t: TFunction;
};

export function FilterField({
  className,
  definition,
  errors,
  onChange,
  t,
}: FilterFieldProps) {
  const field = useFieldContext<boolean | number | string | null>();

  if (definition.type === "boolean") {
    return (
      <FiltersFieldRow
        as={SwitchLabel}
        className={cls(styles.SwitchLabel, className)}
        label={t(definition.translationKey)}
      >
        <Switch
          checked={Boolean(field.state.value)}
          onChange={(e) => onChange(e.currentTarget.checked)}
          readOnly={definition.disabled}
        />
      </FiltersFieldRow>
    );
  }

  if (definition.type === "number") {
    return (
      <NumberField
        className={className}
        definition={definition}
        errors={errors}
        label={t(definition.translationKey)}
        onChangeDebounce={50}
      />
    );
  }

  if (definition.options.length === 0) {
    return null;
  }

  return (
    <Select
      className={cls(styles.Select, className)}
      data-selected-id={field.state.value}
      errors={errors}
      label={formatOptionLabel(definition, t)}
      onChange={(e) => {
        onChange(e.currentTarget.value);
      }}
      value={field.state.value as string}
    >
      {definition.options.map((option) => (
        <option key={option.id} value={option.id}>
          {formatOptionLabel(option, t)}{" "}
          {option.default ? t("defaultOption") : ""}
        </option>
      ))}
    </Select>
  );
}
